import { collection, getDocs, orderBy, query } from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import {
  modalitiesResponseSchema,
  type ModalitiesResponse,
  type Modality,
} from "@/shared/schemas/modalities";

/**
 * Obtiene todas las modalidades desde la colección 'modalities' en Firestore
 * @returns Promise<ModalitiesResponse> - Array de modalidades ordenadas por abreviación
 * @throws Error si hay problemas con la conexión o los datos
 */
export async function getModalities(): Promise<ModalitiesResponse> {
  try {
    // Crear referencia a la colección 'modalities'
    const modalitiesRef = collection(db, "modalities");

    // Crear query ordenada por abreviación
    const q = query(modalitiesRef, orderBy("abbr", "asc"));

    // Obtener documentos
    const querySnapshot = await getDocs(q);

    // Convertir documentos a objetos
    const modalities = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
      };
    });

    // Validar datos con Zod
    const validatedModalities = modalitiesResponseSchema.parse(modalities);

    return validatedModalities;
  } catch (error) {
    console.error("Error al obtener modalidades:", error);
    throw new Error(
      `Error al obtener modalidades: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Obtiene una modalidad específica por su abreviación
 * @param abbr - La abreviación de la modalidad
 * @returns Promise<Modality | null> - La modalidad encontrada o null
 */
export async function getModalityByAbbr(abbr: string): Promise<Modality | null> {
  try {
    const modalities = await getModalities();
    return modalities.find((modality) => modality.abbr === abbr) || null;
  } catch (error) {
    console.error(`Error al obtener modalidad ${abbr}:`, error);
    throw new Error(
      `Error al obtener modalidad ${abbr}: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}
